import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PipelineLevel } from "@/types/pipeline";
import { format, isToday } from "date-fns";
import { ChevronDown, CornerLeftUp } from "lucide-react";
import { useState } from "react";
import { PipelineHeaderFiltersPopover } from "./PipelineHeaderFiltersPopover";

interface FilterDropdownProps {
  label: string;
  options: { value: string | number; label: string }[];
  value: string | number | null;
  onValueChange: (value: string) => void;
  allOptionLabel?: string;
  allOptionValue?: string | number;
}

const FilterDropdown = ({
  label,
  options,
  value,
  onValueChange,
  allOptionLabel,
  allOptionValue,
}: FilterDropdownProps) => {
  const allOptions =
    allOptionLabel && allOptionValue !== undefined
      ? [{ value: allOptionValue, label: allOptionLabel }, ...options]
      : options;

  return (
    <div className={`flex flex-col items-start justify-center`}>
      <div className="text-[10px] leading-[12px] font-medium text-slate-500">{label}</div>
      <Select value={value?.toString() ?? ""} onValueChange={onValueChange}>
        <SelectTrigger className="!h-[17px] border-none p-0 font-medium !text-slate-700 shadow-none focus-visible:ring-0">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent position="popper">
          {allOptions.map((option) => (
            <SelectItem key={option.value} value={option.value.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export type PipelineTab = "summary" | "breakdown";

interface PipelineHeaderProps {
  onMyQueueClick: () => void;
  isCurrentAgentView: boolean;
  onBackClick?: () => void;
  title?: string;
  currentLevel: PipelineLevel;

  activeTab: PipelineTab;
  onTabChange: (tab: PipelineTab) => void;

  selectedBusiness?: string;
  setSelectedBusiness?: (val: string) => void;
  businessOptions?: { value: string; label: string }[];

  selectedHoB: string | null;
  setSelectedHoB: (val: string | null) => void;
  hobOptions: { value: string; label: string }[];

  selectedOSM: string | null;
  setSelectedOSM: (val: string | null) => void;
  osmOptions: { value: string; label: string }[];

  selectedTeamId: string | null;
  setSelectedTeamId: (val: string) => void;
  teamOptions: { value: string; label: string }[];

  selectedAgentId: number | null;
  setSelectedAgentId: (val: number) => void;
  agentOptions: { value: number; label: string }[];

  searchTerm: string;
  setSearchTerm: (term: string) => void;

  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export const PipelineHeader = ({
  onMyQueueClick,
  isCurrentAgentView,
  onBackClick,
  title = "All Heads of Sales",
  currentLevel,
  activeTab,
  onTabChange,
  selectedBusiness = "Whole Business",
  setSelectedBusiness,
  businessOptions = [{ value: "Whole Business", label: "Whole Business" }],
  selectedHoB,
  setSelectedHoB,
  hobOptions,
  selectedOSM,
  setSelectedOSM,
  osmOptions,
  selectedTeamId,
  setSelectedTeamId,
  teamOptions,
  selectedAgentId,
  setSelectedAgentId,
  agentOptions,
  selectedDate,
  onDateChange,
}: PipelineHeaderProps) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const minSelectableDate = new Date();
  minSelectableDate.setHours(0, 0, 0, 0);
  minSelectableDate.setDate(minSelectableDate.getDate() - 7);
  const showBackButton = !!onBackClick;
  const showBusinessDropdown = currentLevel === PipelineLevel.Director;
  const showHoBDropdown = currentLevel === PipelineLevel.Director;
  const showOSMDropdown = currentLevel === PipelineLevel.Director || currentLevel === PipelineLevel.HoB;

  return (
    <div>
      <div className="box-border flex min-h-[52px] w-full flex-col justify-center border-b border-solid border-slate-200 bg-white px-2 py-2 md:px-4">
        <div className="flex h-9 w-full flex-row items-center justify-between gap-2 md:gap-6">
          <div className="flex min-w-0 flex-shrink flex-row items-center gap-2 md:gap-3">
            <button
              onClick={onBackClick}
              disabled={!showBackButton}
              className={`flex-shrink-0 ${
                showBackButton ? "cursor-pointer transition-opacity hover:opacity-70" : "cursor-default text-slate-300"
              }`}
            >
              <CornerLeftUp size={18} />
            </button>

            <div className="my-auto h-[17px] w-px flex-shrink-0 bg-slate-200" />

            <div
              data-testid="pipeline-header-title"
              className="truncate text-sm leading-5 font-bold text-slate-700 md:text-base"
            >
              {title}
            </div>
          </div>

          <div className="flex flex-shrink-0 flex-row items-center gap-2 md:gap-4">
            <div className="hidden flex-row items-center gap-6 md:flex">
              {showBusinessDropdown && (
                <FilterDropdown
                  label="Business"
                  value={selectedBusiness}
                  onValueChange={(val) => setSelectedBusiness && setSelectedBusiness(val)}
                  options={businessOptions}
                />
              )}

              {showHoBDropdown && (
                <FilterDropdown
                  label="Head of Sales"
                  value={selectedHoB || "all"}
                  onValueChange={(val) => {
                    setSelectedHoB(val === "all" ? null : val);
                  }}
                  options={hobOptions}
                  allOptionLabel="All Heads of Sales"
                  allOptionValue="all"
                />
              )}

              {showOSMDropdown && (
                <FilterDropdown
                  label="Sales Manager"
                  value={selectedOSM || "all"}
                  onValueChange={(val) => {
                    setSelectedOSM(val === "all" ? null : val);
                  }}
                  options={osmOptions}
                  allOptionLabel="All Sales Managers"
                  allOptionValue="all"
                />
              )}

              <FilterDropdown
                label="Team"
                value={selectedTeamId || "all"}
                onValueChange={(val) => setSelectedTeamId(val)}
                options={teamOptions}
                allOptionLabel="All Teams"
                allOptionValue="all"
              />

              <FilterDropdown
                label="Agent"
                value={selectedAgentId || -1}
                onValueChange={(val) => setSelectedAgentId(Number(val))}
                options={agentOptions}
                allOptionLabel="All Agents"
                allOptionValue={-1}
              />
            </div>

            <div className="md:hidden">
              <PipelineHeaderFiltersPopover
                showBusinessDropdown={showBusinessDropdown}
                selectedBusiness={selectedBusiness}
                setSelectedBusiness={setSelectedBusiness}
                businessOptions={businessOptions}
                showHoBDropdown={showHoBDropdown}
                selectedHoB={selectedHoB}
                setSelectedHoB={setSelectedHoB}
                hobOptions={hobOptions}
                showOSMDropdown={showOSMDropdown}
                selectedOSM={selectedOSM}
                setSelectedOSM={setSelectedOSM}
                osmOptions={osmOptions}
                selectedTeamId={selectedTeamId}
                setSelectedTeamId={setSelectedTeamId}
                teamOptions={teamOptions}
                selectedAgentId={selectedAgentId}
                setSelectedAgentId={setSelectedAgentId}
                agentOptions={agentOptions}
              />
            </div>

            <div className="hidden items-center gap-4 md:flex">
              <div className="my-auto h-[29px] w-px bg-slate-200" />

              <button
                className={`box-border flex h-7 cursor-pointer items-center justify-center rounded border border-slate-300 p-2 text-sm font-medium hover:bg-slate-50 ${
                  isCurrentAgentView ? "text-slate-300" : "text-slate-700"
                }`}
                onClick={onMyQueueClick}
                disabled={isCurrentAgentView}
              >
                My Queue
              </button>
            </div>
          </div>
        </div>
      </div>

      {currentLevel === PipelineLevel.Agent ? null : (
        <div className="box-border flex h-11 w-full items-center justify-between gap-2 border-b border-solid border-slate-200 bg-white px-2 md:px-4">
          {currentLevel === PipelineLevel.Director ||
          currentLevel === PipelineLevel.HoB ||
          currentLevel === PipelineLevel.OSM ? (
            <Tabs key="dashboard-tabs" value="summary" variant="underline">
              <TabsList>
                <TabsTrigger value="summary">Dashboard</TabsTrigger>
              </TabsList>
            </Tabs>
          ) : (
            <Tabs
              key="team-tabs"
              value={activeTab}
              onValueChange={(val) => onTabChange(val as PipelineTab)}
              variant="underline"
            >
              <TabsList>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <button className="flex flex-shrink-0 cursor-pointer flex-col items-start justify-center">
                <div className="text-[10px] leading-[12px] font-medium text-slate-500">Date</div>
                <div className="flex h-[17px] items-center gap-2">
                  <span className="text-sm leading-[17px] font-medium whitespace-nowrap text-slate-700">
                    {isToday(selectedDate) ? "Today" : format(selectedDate, "dd MMM yyyy")}
                  </span>
                  <ChevronDown size={12} className="text-slate-400" />
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                disabled={{
                  before: minSelectableDate,
                  after: today,
                }}
                onSelect={(date) => {
                  if (date) {
                    onDateChange(date);
                    setDatePickerOpen(false);
                  }
                }}
                defaultMonth={selectedDate}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
};
